import React from 'react';
import { ExternalLink, Globe, Trophy, Wrench, GraduationCap, Users, Plane, Award, Target } from 'lucide-react';
import { firstGlobalFeatures } from '../../data/mock';
import { getOptimizedImageUrl } from '../../lib/utils';

const iconMap = {
  Globe: Globe,
  Trophy: Trophy,
  Wrench: Wrench,
  GraduationCap: GraduationCap,
  Users: Users,
  Plane: Plane
};

const FirstGlobalSection = () => {
  return (
    <section data-testid="first-global-section" className="relative py-20 md:py-32 bg-cream-dark overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_#B07C59_1px,_transparent_1px)] bg-[length:32px_32px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-dwarfpony text-sm font-semibold tracking-wider uppercase mb-4">
            Global Partnership
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-deepwalnut mb-6">
            Exclusive India Partner to <span className="text-richolive">FIRST Global</span>
          </h2>
          <p className="text-heavyrain text-lg max-w-2xl mx-auto">
            Robotics Olympics Competition bringing together teams from 190+ countries
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left - Image & Problem Statement */}
          <div className="space-y-6">
            <div className="relative rounded-2xl overflow-hidden group shadow-xl">
              <img
                src={getOptimizedImageUrl("/assets/images/IMG-20251030-WA0052.jpg", 1200)}
                srcSet={`
                  ${getOptimizedImageUrl("/assets/images/IMG-20251030-WA0052.jpg", 400)} 400w,
                  ${getOptimizedImageUrl("/assets/images/IMG-20251030-WA0052.jpg", 800)} 800w,
                  ${getOptimizedImageUrl("/assets/images/IMG-20251030-WA0052.jpg", 1200)} 1200w
                `}
                sizes="(max-width: 768px) 100vw, 50vw"
                alt="FIRST Global Competition"
                className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/50 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-white font-semibold text-lg">FIRST Global Robotics Challenge</p>
                <p className="text-pragmatic">An Olympics-style global robotics competition</p>
              </div>
            </div>

            {/* Problem Statement CTA */}
            <a
              href="https://first.global/fgc/"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-gradient-to-r from-pragmatic/20 to-richolive/10 border border-dwarfpony/30 rounded-xl p-6 hover:border-dwarfpony transition-colors group shadow-md"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-dwarfpony text-sm font-semibold uppercase tracking-wider">2026 Challenge</p>
                  <p className="text-deepwalnut text-xl font-bold mt-1">Theme Igniting Innovation</p>
                  <p className="text-heavyrain text-sm mt-2">View the FIRST Global 2026 Problem Statement</p>
                </div>
                <ExternalLink className="text-dwarfpony group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={24} />
              </div>
            </a>
          </div>

          {/* Right - About FIRST Global & Features */}
          <div className="space-y-8">
            {/* About FIRST Global Card */}
            <div className="bg-cream border border-pragmatic/30 rounded-2xl p-6 hover:border-richolive hover:shadow-lg transition-all">
              <h3 className="text-deepwalnut font-bold text-lg mb-4">What is FIRST Global?</h3>
              <p className="text-heavyrain text-sm leading-relaxed mb-4">
                FIRST Global is an international robotics competition that brings together teams from over 190 countries
                to work on real-world challenges. Each year, teams design and build robots to solve problems related
                to global issues like clean energy, water sustainability, and environmental conservation.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 bg-gradient-to-r from-pragmatic/20 to-richolive/10 rounded-lg p-3">
                  <Award className="w-8 h-8 text-dwarfpony" />
                  <div>
                    <p className="text-deepwalnut font-bold text-lg">190+</p>
                    <p className="text-heavyrain text-xs">Countries</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-gradient-to-r from-pragmatic/20 to-richolive/10 rounded-lg p-3">
                  <Target className="w-8 h-8 text-richolive" />
                  <div>
                    <p className="text-deepwalnut font-bold text-lg">Since 2017</p>
                    <p className="text-heavyrain text-xs">Annual Event</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {firstGlobalFeatures.map((feature, index) => {
                const Icon = iconMap[feature.icon];
                return (
                  <div
                    key={index}
                    className="bg-cream border border-pragmatic/30 rounded-xl p-4 hover:border-dwarfpony hover:shadow-md transition-all group"
                  >
                    <Icon className="w-6 h-6 text-richolive mb-2 group-hover:text-dwarfpony transition-colors" />
                    <p className="text-deepwalnut font-semibold text-sm">{feature.title}</p>
                    <p className="text-heavyrain text-xs mt-1">{feature.description}</p>
                  </div>
                );
              })}
            </div>

            {/* Link to FIRST Global */}
            <a
              href="https://first.global/about/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-richolive hover:text-dwarfpony transition-colors font-medium"
            >
              Learn more about FIRST Global
              <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FirstGlobalSection;
